import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  Database,
  Trash2,
  AlertTriangle,
  Loader2,
  Calendar,
  Clock,
  ChevronDown,
  MessageSquare,
} from 'lucide-react'
import type { RawRecordsCount, CountBeforeResponse } from '@/types/attendance-metrics'
import api from '@/lib/api'

interface ImportSummaryBarProps {
  rawRecordsCount: RawRecordsCount | undefined
  onClear: (beforeDate: string) => void
  isClearing?: boolean
  isLoading?: boolean
}

export function ImportSummaryBar({
  rawRecordsCount,
  onClear,
  isClearing,
  isLoading,
}: ImportSummaryBarProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedDateTime, setSelectedDateTime] = useState<string>('')
  const [messageCount, setMessageCount] = useState<number | null>(null)
  const [isLoadingCount, setIsLoadingCount] = useState(false)

  const shortcuts = [
    { label: 'Mais de 6 meses', months: 6 },
    { label: 'Mais de 12 meses', months: 12 },
  ]

  // Format date to datetime-local input format (YYYY-MM-DDTHH:mm)
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Parse datetime-local value to display format
  const formatDisplayDate = (datetimeLocal: string): string => {
    if (!datetimeLocal) return ''
    const date = new Date(datetimeLocal)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Fetch message count when selectedDateTime changes (debounced)
  useEffect(() => {
    if (!selectedDateTime) {
      setMessageCount(null)
      return
    }

    setIsLoadingCount(true)

    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.get<CountBeforeResponse>('/attendance-metrics/raw-records/count-before', {
          params: { beforeDate: selectedDateTime },
        })
        setMessageCount(response.data.count)
      } catch {
        setMessageCount(null)
      } finally {
        setIsLoadingCount(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [selectedDateTime])

  const handleShortcutClick = (months: number) => {
    const beforeDate = new Date()
    beforeDate.setMonth(beforeDate.getMonth() - months)
    setSelectedDateTime(formatDateTimeLocal(beforeDate))
    setModalOpen(true)
  }

  const handleCleanupTotalClick = () => {
    setSelectedDateTime(formatDateTimeLocal(new Date()))
    setModalOpen(true)
  }

  const handleCurrentDateClick = () => {
    setSelectedDateTime(formatDateTimeLocal(new Date()))
  }

  const handleConfirm = () => {
    if (!selectedDateTime) return
    onClear(selectedDateTime)
    setModalOpen(false)
    setSelectedDateTime('')
  }

  const canConfirm = selectedDateTime !== ''
  const hasData = rawRecordsCount && rawRecordsCount.total > 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-8 w-24" />
      </div>
    )
  }

  if (!hasData) {
    return (
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
        <Database className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Nenhuma mensagem importada</span>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
        {/* Summary info */}
        <div className="flex items-center gap-3">
          <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm">
              <Badge variant="secondary" className="font-mono">
                {rawRecordsCount.total.toLocaleString()}
              </Badge>
              <span className="ml-2 text-muted-foreground">mensagens importadas</span>
            </span>
            {rawRecordsCount.oldestDate && (
              <span className="text-xs text-muted-foreground">
                · Mais antiga: {new Date(rawRecordsCount.oldestDate).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>

        {/* Cleanup dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isClearing}
              className="shrink-0"
            >
              {isClearing ? (
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3 mr-2" />
              )}
              Limpeza
              <ChevronDown className="h-3 w-3 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Remover mensagens anteriores a:
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {shortcuts.map((shortcut) => (
              <DropdownMenuItem
                key={shortcut.months}
                onClick={() => handleShortcutClick(shortcut.months)}
                className="cursor-pointer"
              >
                <Clock className="h-3 w-3 mr-2" />
                {shortcut.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleCleanupTotalClick}
              className="cursor-pointer !text-red-600 dark:!text-red-400"
            >
              <AlertTriangle className="h-3 w-3 mr-2" />
              Limpeza Total
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Cleanup Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Limpar Mensagens
            </DialogTitle>
            <DialogDescription>
              Selecione a data limite. Todas as mensagens anteriores a esta data serão excluídas
              permanentemente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* DateTime Picker */}
            <div className="space-y-2">
              <Label htmlFor="cleanup-datetime" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Excluir mensagens anteriores a:
              </Label>
              <Input
                id="cleanup-datetime"
                type="datetime-local"
                value={selectedDateTime}
                onChange={(e) => setSelectedDateTime(e.target.value)}
                className="w-full"
              />
              {/* Message count indicator */}
              {selectedDateTime && (
                <div className="text-sm text-muted-foreground">
                  {isLoadingCount ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Calculando...
                    </span>
                  ) : messageCount !== null ? (
                    <Badge variant="secondary" className="font-mono">
                      ~{messageCount.toLocaleString()} mensagens serão afetadas
                    </Badge>
                  ) : null}
                </div>
              )}
            </div>

            <Separator />

            {/* Shortcuts inside modal */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Atalhos:</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {shortcuts.map((shortcut) => (
                  <Button
                    key={shortcut.months}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const beforeDate = new Date()
                      beforeDate.setMonth(beforeDate.getMonth() - shortcut.months)
                      setSelectedDateTime(formatDateTimeLocal(beforeDate))
                    }}
                    type="button"
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    {shortcut.label}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCurrentDateClick}
                  type="button"
                  className="text-red-600 dark:text-red-400"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Limpeza Total
                </Button>
              </div>
            </div>

            <Separator />

            {/* Preview of selected date */}
            {selectedDateTime && (
              <Alert className="bg-muted/50 border-border">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-sm text-foreground">
                  <p>
                    Serão excluídas todas as mensagens anteriores a{' '}
                    <span className="font-semibold">{formatDisplayDate(selectedDateTime)}</span>
                  </p>
                  <p className="text-red-600 dark:text-red-400 font-semibold mt-1">
                    Esta ação não pode ser desfeita.
                  </p>
                  <p className="text-xs mt-2 text-muted-foreground">
                    Relatórios consolidados não são afetados.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={!canConfirm || isClearing}
            >
              {isClearing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Confirmar Exclusão
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
